import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from './LanguageContext';
import { t } from '../utils/translations';
import { Check } from 'lucide-react';
export function PricingTable() {
  const {
    lang
  } = useLanguage();
  const text = t[lang].pricing;
  return <section id="pricing" className="py-24 bg-mf-gray relative overflow-hidden" data-id="element-272">
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-gray-100 to-transparent opacity-50 pointer-events-none" data-id="element-273"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-274">
        <div className="text-center mb-20" data-id="element-275">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          type: 'spring',
          stiffness: 100,
          damping: 15
        }} className="text-4xl md:text-6xl font-condensed font-black text-mf-black uppercase tracking-tight mb-6" data-id="element-276">
            {text.title}
          </motion.h2>
          <motion.div initial={{
          width: 0
        }} whileInView={{
          width: 96
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2,
          duration: 0.8
        }} className="h-1.5 bg-mf-red mx-auto rounded-full" data-id="element-277"></motion.div>
        </div>

        {/* Bento Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8" data-id="element-278">
          {text.cards.map((card, index) => {
          const isPopular = index === 1; // UNIT 2 is popular
          return <motion.div key={index} initial={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} whileInView={{
            opacity: 1,
            scale: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1,
            type: 'spring',
            stiffness: 100,
            damping: 15
          }} className={`relative group rounded-3xl overflow-hidden flex flex-col bg-white shadow-xl hover:shadow-2xl transition-all duration-500 ${isPopular ? 'md:-translate-y-4' : ''}`} data-id="element-279">
                {/* Animated Border Highlight for Popular Card */}
                {isPopular && <>
                    <div className="absolute inset-0 bg-gradient-to-r from-mf-red via-mf-yellow to-mf-red opacity-100 group-hover:opacity-100 transition-opacity duration-500" style={{
                padding: '4px'
              }} data-id="element-280">
                      <div className="absolute inset-0 bg-gradient-to-r from-mf-red via-mf-yellow to-mf-red animate-spin" style={{
                  animationDuration: '4s',
                  filter: 'blur(8px)'
                }} data-id="element-281"></div>
                      <div className="w-full h-full bg-white rounded-[20px] relative z-10 flex flex-col" data-id="element-282">
                        {/* Popular Badge */}
                        <div className="bg-mf-red text-white text-center py-2 font-bold text-sm uppercase tracking-widest w-full rounded-t-[16px]" data-id="element-283">
                          {text.popular}
                        </div>
                        {/* Content */}
                        <CardContent card={card} text={text} isPopular={isPopular} data-id="element-284" />
                      </div>
                    </div>
                  </>}

                {/* Standard Card */}
                {!isPopular && <div className="w-full h-full bg-white rounded-3xl relative z-10 flex flex-col border border-gray-100" data-id="element-285">
                    <CardContent card={card} text={text} isPopular={isPopular} data-id="element-286" />
                  </div>}
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}
// Helper component for card content to avoid repetition
function CardContent({
  card,
  text,
  isPopular
}: {
  card: any;
  text: any;
  isPopular: boolean;
}) {
  return <>
      <div className="p-8 flex-grow flex flex-col" data-id="element-287">
        <h3 className="text-2xl font-condensed font-black uppercase text-mf-black mb-8 border-b-2 border-gray-100 pb-4" data-id="element-288">
          {card.title}
        </h3>

        <ul className="space-y-5 mb-8 flex-grow" data-id="element-289">
          {card.features.map((feature: string, fIndex: number) => <li key={fIndex} className="flex items-start group/item" data-id="element-290">
              <div className={`mt-0.5 mr-4 flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-colors duration-300 ${isPopular ? 'bg-mf-red/10 text-mf-red group-hover/item:bg-mf-red group-hover/item:text-white' : 'bg-gray-100 text-gray-500 group-hover/item:bg-mf-black group-hover/item:text-white'}`} data-id="element-291">
                <Check size={14} strokeWidth={3} data-id="element-292" />
              </div>
              <span className="text-gray-700 font-medium leading-tight pt-0.5" data-id="element-293">
                {feature}
              </span>
            </li>)}
        </ul>
      </div>

      <div className="p-8 pt-0 mt-auto" data-id="element-294">
        <a href="#booking" className={`block w-full text-center py-4 rounded-xl font-condensed font-bold text-lg uppercase tracking-wider transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] ${isPopular ? 'bg-mf-red text-white shadow-[0_4px_14px_0_rgba(211,47,47,0.39)] hover:shadow-[0_6px_20px_rgba(211,47,47,0.23)]' : 'bg-mf-black text-white hover:bg-gray-800 shadow-md'}`} data-id="element-295">
          {text.cta}
        </a>
      </div>
    </>;
}